import type {
  Contact,
  ContactChannelInput,
  ContactInput,
  ContactRelationInput,
} from '@aimmo/contracts';
import {
  CONTACT_CLASSIFICATIONS,
  CONTACT_CLASSIFICATION_LABELS,
  CONTACT_KINDS,
  CONTACT_CHANNEL_KIND_LABELS,
  CONTACT_KIND_LABELS,
  CONTACT_RELATION_TYPE_LABELS,
  DERIVED_ROLE_LABELS,
  contactCreateSchema,
  enumOptions,
} from '@aimmo/contracts';
import { Badge, defineResource, tsRestResourceClient } from '@aimmo/ui';
import { api } from '../lib/api.js';
import { resolveComune, searchComuni } from './geo-lookups.js';
import { resolveContact, searchContacts } from './contact-lookups.js';
import { TaxIdField } from './contact-tax-id-field.js';
import { ContactChannelsField as ChannelsField } from './contact-channels-field.js';
import { ContactRelationsField as RelationsField } from './contact-relations-field.js';
import { ActivityTimeline } from '../features/activity-timeline.js';
import { ContactEmailCard } from './contact-email-card.js';
import { streetLine } from '../lib/format.js';

const KIND_OPTIONS = enumOptions(CONTACT_KINDS, CONTACT_KIND_LABELS);
const CLASSIFICATION_OPTIONS = enumOptions(CONTACT_CLASSIFICATIONS, CONTACT_CLASSIFICATION_LABELS);

/** The stored labels and the computed roles, told apart at a glance. */
function classificationBadges(row: Contact) {
  return row.classifications.map((entry) => (
    <Badge key={entry.classification} tone="neutral">
      {CONTACT_CLASSIFICATION_LABELS[entry.classification]}
    </Badge>
  ));
}

function roleBadges(row: Contact) {
  return row.derivedRoles.map((role) => (
    <Badge key={role} tone="accent">
      {DERIVED_ROLE_LABELS[role]}
    </Badge>
  ));
}

/**
 * People and companies (docs/domain/04-contacts.md).
 *
 * The form asks a person and a company different questions — `visibleWhen`
 * switches on `kind` rather than showing every field with half of them
 * inapplicable.
 *
 * Roles are shown but never edited: they are derived from mandates and demands
 * (ADR-0004), so the column stays empty until M4 and M5 land, by design.
 */
export const contactsResource = defineResource<Contact, ContactInput, ContactInput, ContactInput>({
  key: 'contacts',
  label: { one: 'Contatto', many: 'Contatti', gender: 'm' },
  client: tsRestResourceClient(api.contacts),
  getRowId: (row) => row.id,
  defaultState: { sort: 'displayName', order: 'asc' },
  search: { placeholder: 'Nome, email, telefono, codice fiscale…' },
  columns: [
    { id: 'displayName', header: 'Nome', sortKey: 'displayName' },
    {
      id: 'kind',
      header: 'Tipo',
      width: '9rem',
      cell: ({ row }) => <Badge tone="outline">{CONTACT_KIND_LABELS[row.kind]}</Badge>,
    },
    {
      id: 'classifications',
      header: 'Classificazioni',
      cell: ({ row }) => <div className="flex flex-wrap gap-1">{classificationBadges(row)}</div>,
    },
    {
      id: 'derivedRoles',
      header: 'Ruoli',
      cell: ({ row }) => <div className="flex flex-wrap gap-1">{roleBadges(row)}</div>,
    },
    { id: 'email', header: 'Email', sortKey: 'email' },
    {
      id: 'comune',
      header: 'Comune',
      accessor: (row) => (row.comuneName ? `${row.comuneName} (${row.sigla})` : ''),
    },
    {
      id: 'createdAt',
      header: 'Creato',
      sortKey: 'createdAt',
      width: '10rem',
      hiddenByDefault: true,
      accessor: (row) => new Date(row.createdAt),
    },
  ],
  filters: [
    { id: 'kind', label: 'Tipo', type: 'enum', options: KIND_OPTIONS },
    {
      id: 'classification',
      label: 'Classificazione',
      type: 'enum',
      options: CLASSIFICATION_OPTIONS,
    },
    {
      id: 'comuneId',
      label: 'Comune',
      type: 'relation',
      search: searchComuni,
      resolve: resolveComune,
    },
  ],
  form: {
    schema: contactCreateSchema,
    defaultValues: {
      kind: 'person',
      firstName: '',
      lastName: '',
      companyName: '',
      codiceFiscale: '',
      partitaIva: '',
      channels: [] as ContactChannelInput[],
      addressStreet: '',
      addressNumber: '',
      addressPostalCode: '',
      comuneId: '',
      legalRepContactId: '',
      relations: [] as ContactRelationInput[],
      notes: '',
      classifications: [],
    },
    // The wire shape keeps `classifications` as objects with a date; the form
    // edits the set of labels and the API preserves each `since`.
    fromItem: (contact) => {
      return {
        kind: contact.kind,
        firstName: contact.firstName ?? '',
        lastName: contact.lastName ?? '',
        companyName: contact.companyName ?? '',
        codiceFiscale: contact.codiceFiscale ?? '',
        partitaIva: contact.partitaIva ?? '',
        channels: contact.channels.map((channel) => ({
          kind: channel.kind,
          value: channel.value,
          label: channel.label ?? undefined,
          isPrimary: channel.isPrimary,
        })),
        addressStreet: contact.addressStreet ?? '',
        addressNumber: contact.addressNumber ?? '',
        addressPostalCode: contact.addressPostalCode ?? '',
        comuneId: contact.comuneId ?? '',
        legalRepContactId: contact.legalRepContactId ?? '',
        // Only the ones this contact owns are editable here: the others are the
        // far side's rows, read through the inverse map.
        relations: contact.relations
          .filter((relation) => !relation.inverse)
          .map((relation) => ({
            type: relation.type,
            relatedContactId: relation.relatedContactId,
            notes: relation.notes ?? undefined,
          })),
        notes: contact.notes ?? '',
        classifications: contact.classifications.map((entry) => entry.classification),
      };
    },
    sections: [
      {
        id: 'identity',
        columns: 3,
        title: 'Anagrafica',
        fields: [
          { name: 'kind', label: 'Tipo', type: 'select', required: true, options: KIND_OPTIONS },
          {
            name: 'companyName',
            label: 'Ragione sociale',
            type: 'text',
            required: true,
            visibleWhen: { field: 'kind', equals: ['company'] },
          },
          {
            name: 'firstName',
            label: 'Nome',
            type: 'text',
            visibleWhen: { field: 'kind', equals: ['person'] },
          },
          {
            name: 'lastName',
            label: 'Cognome',
            type: 'text',
            required: true,
            visibleWhen: { field: 'kind', equals: ['person'] },
          },
        ],
      },
      {
        id: 'tax',
        title: 'Dati fiscali',
        description: 'Un codice già presente in archivio viene segnalato, non rifiutato.',
        fields: [
          {
            name: 'codiceFiscale',
            label: 'Codice fiscale',
            type: 'custom',
            render: (props) => (
              <TaxIdField
                id={props.id}
                kind="codiceFiscale"
                value={props.value}
                onChange={props.onChange}
                onBlur={props.onBlur}
                disabled={props.disabled}
                placeholder="RSSMRA85T10A562S"
              />
            ),
          },
          {
            name: 'partitaIva',
            label: 'Partita IVA',
            type: 'custom',
            // Not gated to companies: ditte individuali and professionisti are
            // `person` rows that carry a P.IVA, and the UAT pass found the
            // field simply missing for them.
            render: (props) => (
              <TaxIdField
                id={props.id}
                kind="partitaIva"
                value={props.value}
                onChange={props.onChange}
                onBlur={props.onBlur}
                disabled={props.disabled}
                placeholder="12345678903"
              />
            ),
          },
        ],
      },
      {
        id: 'reach',
        title: 'Recapiti',
        description: 'Quanti ne servono. Il primo di ogni tipo è quello che appare in elenco.',
        columns: 1,
        fields: [
          {
            name: 'channels',
            label: 'Recapiti',
            type: 'custom',
            wide: true,
            render: (props) => (
              <ChannelsField
                value={props.value}
                onChange={(next) => props.onChange(next)}
                disabled={props.disabled}
              />
            ),
          },
        ],
      },
      {
        id: 'address',
        title: 'Indirizzo',
        fields: [
          { name: 'addressStreet', label: 'Via', type: 'text' },
          { name: 'addressNumber', label: 'Civico', type: 'text' },
          { name: 'addressPostalCode', label: 'CAP', type: 'text', placeholder: '20121' },
          {
            name: 'comuneId',
            label: 'Comune',
            type: 'relation',
            search: searchComuni,
            resolve: resolveComune,
          },
        ],
      },
      {
        id: 'relations',
        title: 'Relazioni',
        fields: [
          {
            name: 'legalRepContactId',
            label: 'Rappresentante legale',
            type: 'relation',
            search: searchContacts,
            resolve: resolveContact,
            visibleWhen: { field: 'kind', equals: ['company'] },
          },
          {
            name: 'relations',
            label: 'Relazioni',
            type: 'custom',
            wide: true,
            render: (props) => (
              <RelationsField
                value={props.value}
                onChange={(next) => props.onChange(next)}
                disabled={props.disabled}
              />
            ),
          },
        ],
      },
      {
        id: 'classifications',
        title: 'Classificazioni',
        description:
          'Solo le etichette che nessuna relazione implica. Proprietario, venditore e acquirente sono calcolati.',
        columns: 1,
        fields: [
          {
            name: 'classifications',
            label: 'Classificazioni',
            type: 'multiSelect',
            options: CLASSIFICATION_OPTIONS,
            wide: true,
          },
        ],
      },
      {
        id: 'notes',
        title: 'Note',
        columns: 1,
        fields: [{ name: 'notes', label: 'Note', type: 'textarea', rows: 5, wide: true }],
      },
    ],
  },
  detail: {
    title: (row) => row.displayName,
    after: (row) => <ActivityTimeline subjectType="contact" subjectId={row.id} />,
    sections: [
      {
        id: 'identity',
        title: 'Anagrafica',
        fields: [
          { id: 'kind', label: 'Tipo', accessor: (row) => CONTACT_KIND_LABELS[row.kind] },
          { id: 'codiceFiscale', label: 'Codice fiscale' },
          { id: 'partitaIva', label: 'Partita IVA' },
          {
            id: 'derivedRoles',
            label: 'Ruoli',
            render: (row) =>
              row.derivedRoles.length > 0 ? (
                <div className="flex flex-wrap gap-1">{roleBadges(row)}</div>
              ) : (
                'Nessun ruolo attivo'
              ),
          },
          {
            id: 'classifications',
            label: 'Classificazioni',
            render: (row) =>
              row.classifications.length > 0 ? (
                <div className="flex flex-wrap gap-1">{classificationBadges(row)}</div>
              ) : (
                '—'
              ),
          },
        ],
      },
      {
        id: 'reach',
        title: 'Recapiti',
        render: (row) =>
          row.channels.length === 0 ? (
            <p className="text-secondary text-muted">Nessun recapito.</p>
          ) : (
            <ul className="grid gap-1">
              {row.channels.map((channel) => (
                <li key={channel.id} className="flex flex-wrap items-baseline gap-2">
                  <span className="text-label uppercase tracking-[0.08em] text-faint">
                    {CONTACT_CHANNEL_KIND_LABELS[channel.kind]}
                  </span>
                  <span className="text-body text-ink">{channel.value}</span>
                  {channel.label && (
                    <span className="text-secondary text-muted">{channel.label}</span>
                  )}
                  {channel.isPrimary && <Badge tone="neutral">principale</Badge>}
                </li>
              ))}
            </ul>
          ),
      },
      {
        // Consent to email, and the double opt-in that is the only way to it
        // (M10 / P3).
        id: 'email',
        title: 'Comunicazioni email',
        render: (row) => <ContactEmailCard contactId={row.id} />,
      },
      {
        id: 'address',
        title: 'Indirizzo',
        fields: [
          {
            id: 'address',
            label: 'Via',
            accessor: (row) => streetLine(row.addressStreet, row.addressNumber) || '—',
          },
          { id: 'addressPostalCode', label: 'CAP' },
          {
            id: 'comune',
            label: 'Comune',
            accessor: (row) => (row.comuneName ? `${row.comuneName} (${row.sigla})` : '—'),
          },
        ],
      },
      {
        id: 'relations',
        title: 'Relazioni',
        fields: [
          { id: 'legalRepName', label: 'Rappresentante legale' },
          { id: 'notes', label: 'Note' },
        ],
        renderAfter: (row) =>
          row.relations.length === 0 ? null : (
            <ul className="grid gap-1">
              {row.relations.map((relation) => (
                <li
                  key={relation.id + String(relation.inverse)}
                  className="flex flex-wrap items-baseline gap-2"
                >
                  <span className="text-label uppercase tracking-[0.08em] text-faint">
                    {CONTACT_RELATION_TYPE_LABELS[relation.type]}
                  </span>
                  <span className="text-body text-ink">{relation.relatedName}</span>
                  {relation.notes && (
                    <span className="text-secondary text-muted">{relation.notes}</span>
                  )}
                </li>
              ))}
            </ul>
          ),
      },
    ],
  },
  empty: {
    title: 'Nessun contatto',
    description: 'Persone e aziende con cui lavora l’agenzia. Creane uno per iniziare.',
  },
});
